import {isSafari} from '@braintree/browser-detection'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {type RefObject, useCallback, useEffect, useRef} from 'react'

/**
 * Position data for scroll preservation - uses stable identifiers instead of element references
 */
interface ScrollAnchorPosition {
  /** Stable identifier for the element (data-wrapper-timeline-id value) */
  timelineId: string
  /** Element's offset from top of viewport when captured */
  offsetTop: number
}

/**
 * Find the timeline container element by looking for the issue timeline element.
 * Uses the timeline element's ID which is more stable and performant than class selectors.
 */
function findTimelineContainer(): Element | null {
  // The Timeline component in IssueTimeline.tsx has id="issue-timeline"
  return document.getElementById('issue-timeline')
}

/**
 * Safari does not support CSS scroll anchoring (overflow-anchor), which causes
 * scroll position to jump when content is added above the viewport (e.g., "Load more"
 * or when adding new comments to a timeline with a highlighted comment in the URL).
 *
 * This hook provides utilities to manually preserve scroll position in Safari by:
 * 1. Capturing the position of a visible anchor element before DOM changes
 * 2. Restoring the scroll position after DOM changes to maintain the anchor in view
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/overflow-anchor
 * @see https://caniuse.com/css-overflow-anchor (Safari: No support)
 *
 * @param containerRef - Optional ref to the container element. If not provided, will search for Timeline component.
 */
export function useSafariScrollPreservation(containerRef?: RefObject<HTMLElement | null>) {
  const anchorPositionRef = useRef<ScrollAnchorPosition | null>(null)
  const featureFlagEnabled = isFeatureEnabled('issues_react_safari_scroll_preservation')
  const needsPreservation = featureFlagEnabled && isSafari()

  /**
   * Find the best anchor element in the viewport and return its timeline ID.
   * Looks for timeline items within the container ref or falls back to finding the Timeline component.
   */
  const findAnchorElement = useCallback((): {element: Element; timelineId: string} | null => {
    // Use the provided container ref, or try to find the timeline container
    const container = containerRef?.current ?? findTimelineContainer()

    if (!container) return null

    // Find elements with data-wrapper-timeline-id attribute
    const items = container.querySelectorAll('[data-wrapper-timeline-id]')

    // Find the first item that's in the viewport
    for (const item of items) {
      const rect = item.getBoundingClientRect()
      const timelineId = item.getAttribute('data-wrapper-timeline-id')
      // Check if the item is at least partially visible in the viewport
      if (timelineId && rect.top < window.innerHeight && rect.bottom > 0) {
        return {element: item, timelineId}
      }
    }

    return null
  }, [containerRef])

  /**
   * Find element by timeline ID after DOM has changed
   */
  const findElementByTimelineId = useCallback(
    (timelineId: string): Element | null => {
      const container = containerRef?.current ?? findTimelineContainer()
      if (!container) return null
      return container.querySelector(`[data-wrapper-timeline-id="${timelineId}"]`)
    },
    [containerRef],
  )

  /**
   * Capture the current scroll position relative to a visible anchor element.
   * Call this BEFORE triggering DOM changes (e.g., before "Load more" or adding comments).
   */
  const captureScrollPosition = useCallback(() => {
    if (!needsPreservation) return

    const anchor = findAnchorElement()
    if (!anchor) return

    const rect = anchor.element.getBoundingClientRect()
    anchorPositionRef.current = {
      timelineId: anchor.timelineId,
      offsetTop: rect.top,
    }
  }, [findAnchorElement, needsPreservation])

  /**
   * Restore the scroll position to maintain the anchor element in its original position.
   * Call this AFTER DOM changes have completed.
   *
   * Uses requestAnimationFrame to ensure the DOM has been painted before adjusting scroll.
   */
  const restoreScrollPosition = useCallback(() => {
    if (!needsPreservation) return
    if (!anchorPositionRef.current) return

    const {timelineId, offsetTop} = anchorPositionRef.current

    // Use requestAnimationFrame to ensure DOM has been painted
    requestAnimationFrame(() => {
      // Find the element by its stable ID (it may be a new DOM node after re-render)
      const element = findElementByTimelineId(timelineId)
      if (!element) {
        anchorPositionRef.current = null
        return
      }

      const newRect = element.getBoundingClientRect()
      const scrollAdjustment = newRect.top - offsetTop

      if (scrollAdjustment !== 0) {
        window.scrollBy({
          top: scrollAdjustment,
          behavior: 'instant',
        })
      }

      anchorPositionRef.current = null
    })
  }, [findElementByTimelineId, needsPreservation])

  /**
   * Wrap an async operation with scroll preservation.
   * Automatically captures position before and restores after.
   */
  const preserveScrollPosition = useCallback(
    async <T>(operation: () => Promise<T> | T): Promise<T> => {
      if (!needsPreservation) {
        return operation()
      }

      captureScrollPosition()
      const result = await operation()
      restoreScrollPosition()
      return result
    },
    [captureScrollPosition, needsPreservation, restoreScrollPosition],
  )

  return {
    /** Whether scroll preservation is needed (Safari only) */
    needsPreservation,
    /** Capture scroll position before DOM changes */
    captureScrollPosition,
    /** Restore scroll position after DOM changes */
    restoreScrollPosition,
    /** Wrap an operation with automatic scroll preservation */
    preserveScrollPosition,
  }
}

/**
 * Hook that automatically observes the timeline container for DOM mutations
 * and preserves scroll position in Safari.
 *
 * This is useful for handling real-time updates (new comments via subscription)
 * where we don't have explicit control over when the DOM changes.
 *
 * The key insight is that we must continuously track an anchor element's position
 * on every scroll event, so that when a mutation happens, we already have a valid
 * reference from BEFORE the mutation occurred.
 *
 * IMPORTANT: We use `data-wrapper-timeline-id` as a stable identifier because
 * React replaces DOM elements during re-renders. The timeline ID persists across
 * re-renders while element references become stale.
 *
 * @param timelineRef - Ref to the timeline container element
 */
export function useSafariTimelineScrollObserver(timelineRef: RefObject<HTMLElement | null>) {
  const featureFlagEnabled = isFeatureEnabled('issues_react_safari_scroll_preservation')
  const needsPreservation = featureFlagEnabled && isSafari()
  const anchorPositionRef = useRef<ScrollAnchorPosition | null>(null)
  const observerRef = useRef<MutationObserver | null>(null)
  const isRestoringRef = useRef<boolean>(false)

  /**
   * Find a stable anchor element in the viewport and return its timeline ID
   */
  const findAnchorElement = useCallback((): {element: Element; timelineId: string} | null => {
    const container = timelineRef.current ?? findTimelineContainer()
    if (!container) return null

    // Find elements with data-wrapper-timeline-id attribute
    const items = container.querySelectorAll('[data-wrapper-timeline-id]')

    // Find an element that's solidly in the viewport (not just barely visible)
    for (const item of items) {
      const rect = item.getBoundingClientRect()
      const timelineId = item.getAttribute('data-wrapper-timeline-id')
      if (timelineId && rect.top >= 0 && rect.top < window.innerHeight - 100) {
        return {element: item, timelineId}
      }
    }
    // Fallback to first visible element
    for (const item of items) {
      const rect = item.getBoundingClientRect()
      const timelineId = item.getAttribute('data-wrapper-timeline-id')
      if (timelineId && rect.top < window.innerHeight && rect.bottom > 0) {
        return {element: item, timelineId}
      }
    }
    return null
  }, [timelineRef])

  /**
   * Find element by timeline ID after DOM has changed
   */
  const findElementByTimelineId = useCallback(
    (timelineId: string): Element | null => {
      const container = timelineRef.current ?? findTimelineContainer()
      if (!container) return null
      return container.querySelector(`[data-wrapper-timeline-id="${timelineId}"]`)
    },
    [timelineRef],
  )

  /**
   * Update the anchor position - called on scroll and idle intervals
   * This ensures we always have a valid anchor reference BEFORE mutations happen
   */
  const updateAnchorPosition = useCallback(() => {
    // Don't update while we're in the middle of restoring
    if (isRestoringRef.current) return

    const anchor = findAnchorElement()
    if (!anchor) return

    const rect = anchor.element.getBoundingClientRect()
    anchorPositionRef.current = {
      timelineId: anchor.timelineId,
      offsetTop: rect.top,
    }
  }, [findAnchorElement])

  /**
   * Restore scroll position to maintain anchor element position
   */
  const restorePosition = useCallback(() => {
    if (!anchorPositionRef.current) return

    const {timelineId, offsetTop} = anchorPositionRef.current

    // Find the element by its stable ID (it may be a new DOM node after re-render)
    const element = findElementByTimelineId(timelineId)
    if (!element) {
      anchorPositionRef.current = null
      isRestoringRef.current = false
      return
    }

    const newRect = element.getBoundingClientRect()
    const scrollAdjustment = newRect.top - offsetTop

    if (Math.abs(scrollAdjustment) > 1) {
      // Only adjust if there's a meaningful difference
      window.scrollBy({
        top: scrollAdjustment,
        behavior: 'instant',
      })
    }

    // Clear the restoring flag after a brief delay to allow DOM to settle
    setTimeout(() => {
      isRestoringRef.current = false
      // Re-capture anchor position after restore
      updateAnchorPosition()
    }, 50)
  }, [findElementByTimelineId, updateAnchorPosition])

  // Set up scroll listener to continuously track anchor position
  // Note: We intentionally use scroll event here rather than IntersectionObserver because
  // we need to continuously track the exact viewport offset of elements, not just visibility.
  // IntersectionObserver only reports when elements cross threshold boundaries.
  useEffect(() => {
    if (!needsPreservation) return

    // Capture initial anchor position
    updateAnchorPosition()

    // Update anchor position on scroll
    const handleScroll = () => {
      if (!isRestoringRef.current) {
        updateAnchorPosition()
      }
    }

    // Use passive listener for better performance
    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('scroll', handleScroll)

    // Also update periodically in case of non-scroll DOM changes
    const intervalId = setInterval(() => {
      if (!isRestoringRef.current) {
        updateAnchorPosition()
      }
    }, 500)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      clearInterval(intervalId)
    }
  }, [needsPreservation, updateAnchorPosition])

  // Set up MutationObserver to detect DOM changes and restore scroll
  useEffect(() => {
    if (!needsPreservation) return

    const container = timelineRef.current
    if (!container) return

    const observer = new MutationObserver(mutations => {
      // Only handle childList changes (new nodes added/removed)
      const hasChildListMutation = mutations.some(m => m.type === 'childList' && m.addedNodes.length > 0)

      if (!hasChildListMutation) return
      if (!anchorPositionRef.current) return

      // Set restoring flag to prevent anchor updates during restore
      isRestoringRef.current = true

      // Restore after the DOM has settled (double rAF to ensure paint)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          restorePosition()
        })
      })
    })

    observer.observe(container, {
      childList: true,
      subtree: false, // Only observe direct children changes
    })

    observerRef.current = observer

    return () => {
      observer.disconnect()
      observerRef.current = null
    }
  }, [needsPreservation, restorePosition, timelineRef])

  return {
    needsPreservation,
  }
}
